import type { ReactNode } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { LogOut, LayoutDashboard, Globe, FileText, Users, Package, Settings, Shield, Key, KeyRound } from 'lucide-react';
import type { ApiOrganization, ApiUser } from '@/lib/api-client';
import { useLogout } from '@/hooks/useAuth';
import { usePlatformName } from '@/hooks/useBranding';
import { Button } from '@/components/ui/button';
import { OrgSwitcher } from './OrgSwitcher';
import { CreditMeter } from './CreditMeter';
import { DarkModeToggle } from './DarkModeToggle';
import { ImpersonationBanner } from './ImpersonationBanner';
import { cn } from '@/lib/utils';

const nav = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Ana Sayfa' },
  { to: '/sites', icon: Globe, label: 'Siteler' },
  { to: '/content', icon: FileText, label: 'İçerikler' },
  { to: '/team', icon: Users, label: 'Takım' },
  { to: '/packages', icon: Package, label: 'Ödeme & Eklentiler' },
  { to: '/account/preferences', icon: Settings, label: 'Tercihlerim' },
  { to: '/account/api-keys', icon: Key, label: 'API Key\'ler' },
  { to: '/account/byok-keys', icon: KeyRound, label: 'Kendi AI Keyler' },
];

export function AppShell({
  user,
  orgs,
  activeOrgId,
  children,
}: {
  user: ApiUser;
  orgs: ApiOrganization[];
  activeOrgId: string;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const logout = useLogout();
  const platformName = usePlatformName();
  const active = orgs.find((o) => o.id === activeOrgId);

  const onLogout = async () => {
    await logout.mutateAsync();
    navigate({ to: '/login' });
  };

  return (
    <div className="min-h-screen bg-background">
      <ImpersonationBanner />
      {/* Top header */}
      <header className="border-b bg-background/80 backdrop-blur sticky top-0 z-30">
        <div className="flex items-center justify-between h-14 px-4 gap-4">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="font-semibold text-primary">
              {platformName}
            </Link>
            <OrgSwitcher orgs={orgs} activeOrgId={activeOrgId} />
          </div>
          <div className="flex items-center gap-3">
            {active && <CreditMeter balance={active.credit_balance} />}
            {user.platform_role === 'owner' && (
              <Button variant="outline" size="sm" asChild>
                <Link to="/platform">
                  <Shield className="w-4 h-4" />
                  <span className="hidden sm:inline">Platform</span>
                </Link>
              </Button>
            )}
            <DarkModeToggle />
            <div className="text-sm text-muted-foreground hidden sm:block">{user.email}</div>
            <Button variant="ghost" size="icon" onClick={onLogout} title="Çıkış">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-56 border-r h-[calc(100vh-3.5rem)] sticky top-14 bg-muted/30">
          <nav className="p-3 space-y-1">
            {nav.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                // Active + hover styles are emitted via data-status which
                // TanStack Router toggles — keeps active links from being
                // repainted by hover:bg-accent and losing contrast.
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors',
                  'data-[status=active]:bg-primary data-[status=active]:text-primary-foreground data-[status=active]:font-medium',
                  'data-[status=active]:hover:bg-primary data-[status=active]:hover:text-primary-foreground',
                  '[&:not([data-status=active])]:hover:bg-accent [&:not([data-status=active])]:hover:text-accent-foreground',
                )}
              >
                <item.icon className="w-4 h-4" />
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>
        </aside>

        {/* Main */}
        <main className="flex-1 p-6">{children}</main>
      </div>
    </div>
  );
}
